import * as React from 'react';
import { formatDataPointAsMetricNumber, formatRefreshedSince } from '../formatting/formatting';
import {
  getLatestValue,
  computeMetricComparison,
  DataTable,
  DataRow,
  shouldRenderFirstThresholdLine,
  ValueAccessor,
  getStylePropsForDefaultThresholdLine,
  makeVisualTableExtender,
  MetricsNeutralColor,
  MetricsGrey,
  buildCardAccessors,
  buildStatusIndicatorBarModel,
  buildCardColors,
  ThresholdStatus,
  getStatus,
  CONSTANT_COMPARISONS_FLAG,
  COLOR_INDICATORS_FLAG,
  getLineStyleForDefaultThresholdLine,
  LineStyle,
} from '@tableau/metrics-engine';
import { isSingleNumberMetric } from '../utils';
import { Messages } from '../../gen-src/Localize';
import { ResizableSparkLine } from '../SparkLine';
import { MetricCardProps } from './MetricCardProps';
import { CardDeltaLabel } from './CardDeltaLabel';
import { StatusIndicatorBar, STATUS_INDICATOR_HEIGHT } from './StatusIndicatorBar';
import { SvgIcon, SvgIconName } from '../resources/SvgIcon';

export interface MetricBaseCardProps extends MetricCardProps {
  /**
   * outer container
   */
  readonly containerStyle?: React.CSSProperties;
  /**
   * BAN style when there is a timeseries
   */
  readonly banStyle?: React.CSSProperties;
  /**
   * BAN style when there is a single number or no timeseries
   */
  readonly singleNumberBanStyle?: React.CSSProperties;
  /**
   * Style of the container surrounding the sparkline
   */
  readonly timeSeriesContainerStyle?: Pick<React.CSSProperties, 'height' | 'marginTop'>;

  /**
   * The right margin for the time series, which static threshold lines will extend through
   */
  readonly timeSeriesRightMargin?: number;
  /**
   * Style of the bottom label on the card
   */
  readonly labelStyle?: React.CSSProperties;

  readonly dotSize?: number;
}

const DEFAULT_RIGHT_MARGIN = 40;

interface BanModel {
  isSingleNumber: boolean;
  formattedNumber: string;
  color: string;
}

/**
 * Categories of props:
 *
 * - Data Stuff (DataTable, Accessors, Definition, Measure Format String)
 *   - This stuff is shared between Phoenix + the Web
 *   - This is "the model"
 *   - Goal: single function in engine per view that produces the model
 * - Layout Stuff (Height, Width, Style overrides)
 *   - Not Shared
 *   - May be necessary to compute the model, or at least to compute the data (table extender, for one)
 * - Feature Flags
 *   - May be necessary to compute the model
 *   - Many of these may be shared between Phoenix + the Web
 *   - Possible some may be platform-specific?
 */

export class MetricBaseCard extends React.PureComponent<MetricBaseCardProps> {
  public render(): JSX.Element | null {
    const definition = this.props.definition;
    const featureFlags = {
      [CONSTANT_COMPARISONS_FLAG]: this.props.metricsReferenceLineEnabled ?? true,
      [COLOR_INDICATORS_FLAG]: this.props.metricColorIndicatorsEnabled ?? false,
    };

    const accessors = buildCardAccessors(definition, featureFlags);

    if (!accessors.measureAccessor) {
      return null;
    }

    let data: DataTable = [];
    if (this.props.data && typeof this.props.data !== 'number') {
      data = this.props.data;
    }
    const latestValue = getLatestValue(data);

    const statusIndicatorModel = buildStatusIndicatorBarModel(definition, latestValue);
    const statusIndicatorProps = {
      ...statusIndicatorModel,
      featureFlags,
    };

    const banModel = this.buildBanModel(accessors.measureAccessor, latestValue);

    const { deltaColor, dotColor, lineColor } = buildCardColors(definition, latestValue, featureFlags);

    const thresholdStatus = getStatus(definition, featureFlags, latestValue);

    const thresholdLineStyle = getLineStyleForDefaultThresholdLine(definition);
    const lineStyle = thresholdLineStyle?.lineStyle;
    const comparisonLineStyleToRender = {
      ...getStylePropsForDefaultThresholdLine(definition),
      ...(lineStyle === LineStyle.DASHED ? { strokeWidth: 3 } : {}),
    };

    const actualTimeSeriesContainerStyle = { ...timeSeriesContainerStyle, ...this.props.timeSeriesContainerStyle };

    const rightMargin = this.props.timeSeriesRightMargin ?? DEFAULT_RIGHT_MARGIN;

    return (
      <div
        data-tb-test-id={this.props.testId}
        style={{
          ...containerStyle,
          ...this.props.containerStyle,
          ...(featureFlags[COLOR_INDICATORS_FLAG] ? {} : { alignItems: 'center' }),
        }}
      >
        <StatusIndicatorBar {...statusIndicatorProps} />
        <div
          style={{
            ...banAndDeltaContainerStyle,
            ...(featureFlags[COLOR_INDICATORS_FLAG] ? leftMargin : { alignItems: 'center', textAlign: 'center' }),
          }}
        >
          {this.renderBan(banModel)}
          {this.renderDeltaLabel(
            deltaColor,
            thresholdStatus,
            latestValue,
            accessors.measureAccessor,
            accessors.comparisonAccessor
          )}
        </div>

        {!isSingleNumberMetric(this.props.data) && accessors.timestampAccessor && (
          <div aria-label={Messages.ariaTimeseriesDescription()} style={{ width: '100%' }}>
            <div style={actualTimeSeriesContainerStyle} aria-hidden>
              <ResizableSparkLine
                data={this.props.data as ReadonlyArray<DataRow>}
                timestampAccessor={accessors.timestampAccessor}
                measureAccessor={accessors.measureAccessor}
                comparisonAccessor={accessors.comparisonAccessor}
                shouldRenderReferenceLine={
                  this.props.metricsReferenceLineEnabled ? shouldRenderFirstThresholdLine(this.props.definition) : false
                }
                shouldRenderComparisonDot={!thresholdLineStyle?.hidden && thresholdLineStyle?.interactive}
                formatString={this.props.measureFormat}
                strokeWidth={2}
                color={dotColor}
                lineColor={lineColor}
                testId="time-series"
                comparisonLineStyle={comparisonLineStyleToRender}
                rightMargin={rightMargin}
                tableExtender={makeVisualTableExtender(this.props.definition)}
                colorIndicatorsEnabled={this.props.metricColorIndicatorsEnabled}
                dotSize={this.props.dotSize ?? 4}
                haloRatio={2.5}
              />
            </div>
          </div>
        )}

        <div
          data-tb-test-id={'metric-card-label'}
          style={{
            ...labelStyle,
            ...this.props.labelStyle,
            ...(featureFlags[COLOR_INDICATORS_FLAG] ? leftMargin : {}),
          }}
        >
          {this.renderLabel()}
        </div>
      </div>
    );
  }

  private buildBanModel(measureAccessor: ValueAccessor<number>, latestValue: DataRow | undefined): BanModel {
    const isSingleNumber = isSingleNumberMetric(this.props.data);
    const color = this.props.metricColorIndicatorsEnabled ? MetricsGrey : MetricsNeutralColor;
    const formattedNumber = formatDataPointAsMetricNumber(measureAccessor, latestValue, this.props.measureFormat);
    return { isSingleNumber, formattedNumber, color };
  }

  private renderBan({ isSingleNumber, formattedNumber, color }: BanModel): React.ReactElement {
    return (
      <div
        data-tb-test-id={'metric-card-ban'}
        style={{
          ...(isSingleNumber ? singleNumberBanStyle : banStyle),
          ...(isSingleNumber ? this.props.singleNumberBanStyle : this.props.banStyle),
          ...{ color: color },
        }}
      >
        {/* Single number metric gets a div so as to be vertically centered properly with a flexbox */}
        {(isSingleNumber && <div style={{ alignSelf: 'center' }}>{formattedNumber}</div>) || formattedNumber}
      </div>
    );
  }

  private renderDeltaLabel(
    color: string,
    status: ThresholdStatus,
    latestValue: DataRow | undefined,
    measureAccessor: ValueAccessor<number>,
    comparisonAccessor?: ValueAccessor<number | undefined>
  ): JSX.Element | undefined {
    const delta = latestValue ? computeMetricComparison(latestValue, measureAccessor, comparisonAccessor) : undefined;

    return (
      <CardDeltaLabel
        definition={this.props.definition}
        comparisonDelta={delta}
        formatString={this.props.measureFormat}
        color={color}
        status={status}
      />
    );
  }

  private renderLabel(): JSX.Element | string {
    if (this.props.suspended) {
      return (
        <>
          <SvgIcon
            name={SvgIconName.SUSPENDED}
            altText={''}
            style={{ verticalAlign: 'middle', marginRight: '4px' }}
            testId={'metric-card-suspended-warning'}
          />
          {Messages.refreshSuspended()}
        </>
      );
    } else if (this.props.formattedLabel) {
      return this.props.formattedLabel;
    } else if (this.props.lastUpdatedTimestamp) {
      return formatRefreshedSince(this.props.lastUpdatedTimestamp);
    } else {
      return '';
    }
  }
}

const leftMargin: React.CSSProperties = {
  marginLeft: '16px',
};

const timeSeriesContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '38px',
  marginTop: '8px',
  boxSizing: 'border-box',
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
};

const banAndDeltaContainerStyle: React.CSSProperties = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

const banStyle: React.CSSProperties = {
  fontSize: '34px',
  marginBottom: '8px',
  marginTop: 24 - STATUS_INDICATOR_HEIGHT + 'px',
  fontWeight: 'lighter',
  lineHeight: 'normal',
};

const singleNumberBanStyle: React.CSSProperties = {
  ...banStyle,
  fontSize: '40px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#666666',
  marginTop: '10px',
  paddingBottom: '10px',
  fontWeight: 'lighter',
};
